import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';

const DiAiOnboarding = ({ onComplete, onCancel, onClose, isGoogleUser = false, existingUserData = null, hasPassword: hasPasswordProp, isReturningUser = false }) => {
  const hasPassword = hasPasswordProp !== undefined ? hasPasswordProp : (!!existingUserData || isGoogleUser);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: existingUserData?.first_name || '',
    last_name: existingUserData?.last_name || '',
    email: existingUserData?.email || '',
    password: '',
    timezone: existingUserData?.timezone || 'America/Denver',
    primary_role: '',
    main_goal: '',
    genres: '',
    experience_level: '',
    project_stage: '',
    main_challenges: '',
    target_audience: '',
    publishing_platforms: '',
    ai_usage_preference: '',
    email_opt_in: true  // Default to "Yes, keep me updated"
  });

  const [currentAnswer, setCurrentAnswer] = useState('');
  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Scroll to top when modal opens
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Get user's first name - handle cases like "R. Dale" (first initial + middle name)
  const getFirstName = (fullName) => {
    if (!fullName) return 'there';
    
    const parts = fullName.trim().split(' ');
    
    // If only one part, use it
    if (parts.length === 1) return parts[0];
    
    // Check if first part is an initial (single letter or letter with period)
    const firstPart = parts[0];
    const isInitial = firstPart.length <= 2 && (firstPart.length === 1 || firstPart.endsWith('.'));
    
    // If first part is an initial and there's a second part, use "Initial + Second" (e.g., "R. Dale")
    if (isInitial && parts.length > 1) {
      return `${parts[0]} ${parts[1]}`;
    }
    
    // Otherwise, just use the first part
    return firstPart;
  };

  const firstName = getFirstName(formData.first_name || existingUserData?.first_name);

  // Conversational prompts that build on each other
  const conversationalSteps = [
    // Step 0: Welcome
    {
      diaiMessage: () => {
        // Returning unregistered user (used utilities, cancelled onboarding, or was referred)
        if (isReturningUser) {
          return `Welcome, ${firstName}! I'm DiAi (Dee-ah). I'm so glad you're here to complete your onboarding with Digital Ink AI. Let's get you started with a few questions to personalize your experience.`;
        }
        // Brand new user (first time interaction)
        return `Hi ${firstName}! I'm DiAi (Dee-ah). Welcome to Digital Ink AI! I'm your personal guide and I'm excited to help you get started. Let me ask you a few quick questions so we can personalize your experience and help you make the most of our platform.`;
      },
      prompt: null,
      field: null,
      type: 'welcome',
      skipCondition: () => false
    },
    
    // Step 1: Basic Info (if needed)
    {
      diaiMessage: () => hasPassword 
        ? `Great! I already have your basic information. Let's learn more about you as an author.`
        : `First, let's get your basic information so I can personalize your experience.`,
      prompt: () => hasPassword ? null : 'What\'s your full name?',
      placeholder: 'First and Last Name',
      field: 'full_name',
      type: 'text',
      skipCondition: () => hasPassword,
      validation: (value) => {
        if (!hasPassword && (!value || value.trim().length < 2)) {
          return 'Please enter your full name';
        }
        return null;
      }
    },

    // Step 2: Email (if needed)
    {
      diaiMessage: () => hasPassword 
        ? null 
        : `Thanks, ${firstName}! Now I need your email address.`,
      prompt: () => hasPassword ? null : 'What email address would you like to use for your account?',
      placeholder: 'your.email@example.com',
      field: 'email',
      type: 'email',
      skipCondition: () => hasPassword,
      validation: (value) => {
        if (!hasPassword && (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))) {
          return 'Please enter a valid email address';
        }
        return null;
      }
    },

    // Step 3: Password (if needed)
    {
      diaiMessage: () => hasPassword 
        ? null 
        : `Perfect! Now create a secure password for your account.`,
      prompt: () => hasPassword ? null : 'Choose a password (at least 6 characters)',
      placeholder: 'Enter password',
      field: 'password',
      type: 'password',
      skipCondition: () => hasPassword,
      validation: (value) => {
        if (!hasPassword && (!value || value.length < 6)) {
          return 'Password must be at least 6 characters';
        }
        return null;
      }
    },

    // Step 4: Primary Role
    {
      diaiMessage: () => `${firstName}, I'd love to know more about you. What best describes your role in the publishing world?`,
      prompt: () => 'Select the role that best fits you:',
      field: 'primary_role',
      type: 'choice',
      options: [
        'Author/Writer',
        'Publisher/Editor',
        'Student/Learner',
        'Reader/Enthusiast',
        'Other'
      ],
      followUp: (value) => {
        const messages = {
          'Author/Writer': `Excellent! As an author, you'll love our ideation tools, manuscript analysis, and marketing features. They're designed specifically for writers like you.`,
          'Publisher/Editor': `Wonderful! Our competitive analysis and professional editing insights will help you make informed decisions and streamline your quality outputs.`,
          'Student/Learner': `Great to have you! Our genre classification and chapter planning tools will help you master the craft at your own pace.`,
          'Reader/Enthusiast': `How exciting! Our sentiment analysis tools will give you deeper insights into what makes books truly resonate with readers.`,
          'Other': `No problem! Digital Ink AI adapts to support your unique needs in the publishing world, whatever they may be.`
        };
        return messages[value] || '';
      }
    },

    // Step 5: Main Goal
    {
      diaiMessage: () => `That's great, ${firstName}! Now, what's your main goal with Digital Ink AI?`,
      prompt: () => 'What do you want to accomplish?',
      field: 'main_goal',
      type: 'choice',
      options: [
        'Generate book ideas and concepts',
        'Analyze and improve manuscript viability',
        'Create marketing and promotional content',
        'Learn about AI in publishing',
        'Other'
      ],
      followUp: (value) => {
        const messages = {
          'Generate book ideas and concepts': `Perfect! Our Book Concept Generator and Character Creator will be your best friends. They'll help you develop market-savvy concepts that stand out.`,
          'Analyze and improve manuscript viability': `Smart choice! Our Book Viability Analysis and Developmental Editor tools will help you understand market fit and identify opportunities before publishing.`,
          'Create marketing and promotional content': `Excellent! Our Marketing Media Kit, Social Media Kit, and Metadata Generator will help you amplify your book's reach effectively.`,
          'Learn about AI in publishing': `Fantastic! Each service in our Help Center includes step-by-step guides, examples, and best practices. DiAi is here to guide you every step of the way.`,
          'Other': `I understand. Digital Ink AI offers flexible tools that adapt to whatever you need. You'll discover the right services as you explore.`
        };
        return messages[value] || '';
      }
    },

    // Step 6: Genres
    {
      diaiMessage: () => `Interesting! ${formData.main_goal ? 'Based on your goal, ' : ''}what genre(s) do you primarily write or work with?`,
      prompt: () => 'Choose your primary genre:',
      field: 'genres',
      type: 'choice',
      options: [
        'Fiction (Fantasy, Romance, Mystery)',
        'Non-Fiction (Self-Help, Business, Biography)',
        'Science Fiction/Dystopian',
        'Historical/Adventure',
        'Young Adult/Teen',
        'Multiple genres',
        'Not sure yet'
      ],
      followUp: (value) => {
        const messages = {
          'Fiction (Fantasy, Romance, Mystery)': `Fiction writers are the heart of storytelling! Our story structure planning and character development tools will help you craft compelling narratives.`,
          'Non-Fiction (Self-Help, Business, Biography)': `Non-fiction is powerful! Our market trend analysis and competitive positioning tools will help you create resonant, impactful content.`,
          'Science Fiction/Dystopian': `Sci-fi is exciting! Our world-building support and genre-specific analysis will help you bring your visions to life with professional polish.`,
          'Historical/Adventure': `Love it! Our research integration and authenticity tools will help you craft engaging narratives that transport readers.`,
          'Young Adult/Teen': `YA is such a vibrant genre! Our audience targeting and social media tools will help you connect with teen readers effectively.`,
          'Multiple genres': `A versatile writer! Digital Ink AI supports all genres with flexible tools that adapt to each project's unique needs.`,
          'Not sure yet': `That's perfectly fine! Exploring different genres is part of the creative journey. Our tools work across all genres.`
        };
        return messages[value] || '';
      }
    },

    // Step 7: Experience Level
    {
      diaiMessage: () => `Thanks for sharing that, ${firstName}! ${formData.genres ? 'As a ' + formData.genres.toLowerCase() + ' writer, ' : ''}how would you describe your experience level?`,
      prompt: () => 'What\'s your experience level?',
      field: 'experience_level',
      type: 'choice',
      options: [
        'Beginner (just starting out)',
        'Intermediate (a few projects completed)',
        'Advanced (published multiple books)',
        'Professional (full-time author/publisher)'
      ],
      followUp: (value) => {
        const messages = {
          'Beginner (just starting out)': `Perfect place to start! Our beginner-friendly tools and comprehensive Help Center will ease you into publishing with confidence. DiAi will guide you through each service.`,
          'Intermediate (a few projects completed)': `Ready to level up! Our viability analysis and marketing tools will help you grow your craft and expand your readership strategically.`,
          'Advanced (published multiple books)': `Impressive! Our professional editing insights and advanced marketing intelligence will help you optimize your expertise and reach even more readers.`,
          'Professional (full-time author/publisher)': `Welcome, pro! Our premium services and workflow tools will help you scale efficiently and maximize your publishing operations.`
        };
        return messages[value] || '';
      }
    },

    // Step 8: Project Stage
    {
      diaiMessage: () => `Great! ${formData.experience_level ? 'With your ' + formData.experience_level.toLowerCase() + ' experience, ' : ''}where are you currently in your publishing journey?`,
      prompt: () => 'What stage are you at right now?',
      field: 'project_stage',
      type: 'choice',
      options: [
        'Ideation/Brainstorming',
        'Outlining/Planning',
        'Drafting/Writing',
        'Editing/Revision',
        'Marketing/Publishing',
        'Not working on a project yet'
      ],
      followUp: (value) => {
        const messages = {
          'Ideation/Brainstorming': `The beginning is so exciting! Our Book Concept Generator and Character Creator will spark creativity and help you develop trend-aligned ideas.`,
          'Outlining/Planning': `Structure is crucial! Our Book Outline & Chapter Planner will help you create cohesive chapter outlines and organize your narrative for maximum impact.`,
          'Drafting/Writing': `The creative flow! Our Developmental Editor provides insights as you write, helping add depth and ensure your manuscript stays on track.`,
          'Editing/Revision': `Polish time! Our Book Viability Analysis and Professional Developmental Editor will help you make precise improvements based on market insights.`,
          'Marketing/Publishing': `Launch mode! Our Marketing Media Kit, Social Media Kit, and Amazon KDP Wizard will ensure your book reaches its audience successfully.`,
          'Not working on a project yet': `Perfect timing to explore! Browse our services to discover market trends and audience insights that could kickstart your next great idea.`
        };
        return messages[value] || '';
      }
    },

    // Step 9: Main Challenges
    {
      diaiMessage: () => `Thanks for sharing, ${firstName}! ${formData.project_stage ? 'At the ' + formData.project_stage.toLowerCase() + ' stage, ' : ''}what are your biggest challenges in the publishing process?`,
      prompt: () => 'What challenges do you face most?',
      field: 'main_challenges',
      type: 'choice',
      options: [
        'Finding unique book concepts',
        'Structuring my story effectively',
        'Standing out in a crowded market',
        'Understanding what readers want',
        'Marketing and promotion',
        'Time management and productivity',
        'Not sure yet'
      ],
      followUp: (value) => {
        const messages = {
          'Finding unique book concepts': `That's a common challenge! Our Book Concept Generator and Competitive Analysis tools will help you discover fresh, market-aligned ideas that stand out.`,
          'Structuring my story effectively': `Structure is key! Our Book Outline & Chapter Planner and Developmental Editor will help you build a cohesive narrative with proper pacing.`,
          'Standing out in a crowded market': `Great awareness! Our Market Metrics Generator and Competitive Analysis tools will help you identify opportunities and position your work uniquely.`,
          'Understanding what readers want': `Reader insight is crucial! Our Book Viability Analysis and Market Research tools reveal audience preferences and trends to inform your decisions.`,
          'Marketing and promotion': `Marketing can be overwhelming! Our Marketing Media Kit and Social Media Kit simplify the process and help you reach your target readers effectively.`,
          'Time management and productivity': `Efficiency matters! Our streamlined workflow tools and à la carte pricing let you focus on what you do best while we handle the rest.`,
          'Not sure yet': `That's okay! As you explore our services, you'll discover which tools address your specific needs. DiAi is here to help you figure it out.`
        };
        return messages[value] || '';
      }
    },

    // Step 10: Target Audience
    {
      diaiMessage: () => `Got it! ${formData.main_challenges ? 'Knowing your challenges helps us serve you better. ' : ''}Who is your primary target audience?`,
      prompt: () => 'Who are you writing for?',
      field: 'target_audience',
      type: 'choice',
      options: [
        'Children (ages 0-12)',
        'Teens/Young Adults (ages 13-18)',
        'Adults (ages 18-65)',
        'Seniors (65+)',
        'General audience (all ages)',
        'Business professionals',
        'Not sure yet'
      ],
      followUp: (value) => {
        const messages = {
          'Children (ages 0-12)': `Children's books are wonderful! Our age-appropriate language tools and illustration guidance will help you create engaging, developmentally suitable content.`,
          'Teens/Young Adults (ages 13-18)': `YA is vibrant! Our trending topic analysis and social media marketing tools will help you connect with teen readers in their language and on their platforms.`,
          'Adults (ages 18-65)': `The broadest market! Our genre-specific insights and sophisticated marketing tools will help you reach adult readers with compelling, mature content.`,
          'Seniors (65+)': `A valuable demographic! Our accessibility optimization and targeted marketing strategies will help you create content that resonates with experienced readers.`,
          'General audience (all ages)': `Universal appeal! Our cross-demographic analysis tools will help you craft stories that bridge age gaps and connect with diverse readers.`,
          'Business professionals': `The professional market! Our business-focused content strategies and LinkedIn marketing tools will help you reach decision-makers and industry leaders.`,
          'Not sure yet': `That's perfectly fine! Understanding your audience evolves over time. Our analytics tools will help you identify who responds best to your work.`
        };
        return messages[value] || '';
      }
    },

    // Step 11: Publishing Platforms
    {
      diaiMessage: () => `Excellent! ${formData.target_audience ? 'With your target audience in mind, ' : ''}which publishing platform(s) are you using or planning to use?`,
      prompt: () => 'Where will you publish?',
      field: 'publishing_platforms',
      type: 'choice',
      options: [
        'Amazon KDP (Kindle Direct Publishing)',
        'Traditional publishing house',
        'IngramSpark',
        'Draft2Digital',
        'Multiple platforms',
        'Not sure yet',
        'Other platforms'
      ],
      followUp: (value) => {
        const messages = {
          'Amazon KDP (Kindle Direct Publishing)': `Smart choice! Amazon is the largest market. Our Amazon KDP Submission Wizard will optimize your metadata, keywords, and categories for maximum discoverability.`,
          'Traditional publishing house': `The traditional route! Our Manuscript Media Kit Generator will help you create professional query packages that stand out to agents and publishers.`,
          'IngramSpark': `Great for wide distribution! Our print-ready formatting tools and ISBN management features will streamline your IngramSpark workflow.`,
          'Draft2Digital': `Excellent aggregator! Our multi-format optimization tools ensure your book looks perfect across all the retailers Draft2Digital distributes to.`,
          'Multiple platforms': `Maximum reach strategy! Our cross-platform optimization tools and unified metadata management will keep your listings consistent everywhere.`,
          'Not sure yet': `No problem! We'll help you understand the pros and cons of each platform. Many authors start with Amazon KDP and expand from there.`,
          'Other platforms': `Every platform has unique requirements! Our flexible formatting and metadata tools adapt to whatever platform you choose.`
        };
        return messages[value] || '';
      }
    },

    // Step 12: AI Usage Preference
    {
      diaiMessage: () => `Almost done, ${firstName}! ${formData.publishing_platforms ? 'Now that I know your platform preferences, ' : ''}how would you like to use AI in your publishing process?`,
      prompt: () => 'What\'s your preferred approach?',
      field: 'ai_usage_preference',
      type: 'choice',
      options: [
        'As a brainstorming partner',
        'For analysis and feedback',
        'To generate marketing materials',
        'Minimally, only for specific tasks',
        'Extensively, for multiple stages',
        'Not sure yet'
      ],
      followUp: (value) => {
        const messages = {
          'As a brainstorming partner': `Creative collaboration at its best! Our ideation tools and competitive analysis will help you spark and refine your best concepts together with DiAi.`,
          'For analysis and feedback': `Smart approach! Our viability analysis and developmental editing tools provide actionable insights to strengthen your work before publication.`,
          'To generate marketing materials': `Marketing made easier! Our Marketing Kit, Social Media Kit, and Metadata Generator will help you reach readers effectively without the overwhelm.`,
          'Minimally, only for specific tasks': `Your choice, your pace! We offer à la carte pricing so you can use exactly what you need, when you need it. No pressure to use everything.`,
          'Extensively, for multiple stages': `Maximum support! Our subscription gives you unlimited access to standard services and discounts on premium features throughout your entire journey.`,
          'Not sure yet': `That's completely okay! Take your time exploring. Each service has a detailed guide in our Help Center, and DiAi is always here to help you decide.`
        };
        return messages[value] || '';
      }
    },

    // Step 13: Email Opt-in
    {
      diaiMessage: () => `One last thing, ${firstName}! We occasionally send software updates, new feature announcements, and helpful publishing tips.`,
      prompt: () => 'Would you like to receive occasional updates from Digital Ink AI?',
      field: 'email_opt_in',
      type: 'choice',
      options: [
        'Yes, keep me updated!',
        'No, thanks'
      ],
      followUp: (value) => {
        if (value === 'Yes, keep me updated!') {
          return `Wonderful! We'll send you helpful updates and tips. You can unsubscribe anytime from your profile settings. Welcome to the Digital Ink AI community, ${firstName}! 🎉`;
        } else {
          return `No problem! You can always opt-in later from your profile settings. Welcome to Digital Ink AI, ${firstName}! 🎉`;
        }
      }
    }
  ];

  // Filter out skipped steps
  const activeSteps = conversationalSteps.filter(step => !step.skipCondition || !step.skipCondition());
  const currentStepData = activeSteps[currentStep];

  const handleNext = async () => {
    // Validation for current step
    if (currentStepData.type !== 'welcome' && currentStepData.validation) {
      const error = currentStepData.validation(currentAnswer);
      if (error) {
        setErrors({ [currentStepData.field]: error });
        return;
      }
    }

    // Save answer to formData
    if (currentStepData.field && currentAnswer) {
      if (currentStepData.field === 'full_name' && !hasPassword) {
        const names = currentAnswer.trim().split(' ');
        setFormData(prev => ({
          ...prev,
          first_name: names[0],
          last_name: names.slice(1).join(' ') || names[0]
        }));
      } else if (currentStepData.field === 'email_opt_in') {
        const optIn = currentAnswer === 'Yes, keep me updated!';
        console.log('Email opt-in choice:', { currentAnswer, optIn });
        setFormData(prev => ({
          ...prev,
          email_opt_in: optIn
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [currentStepData.field]: currentAnswer
        }));
      }
    }

    // Move to next step or complete
    if (currentStep < activeSteps.length - 1) {
      setCurrentStep(currentStep + 1);
      setCurrentAnswer('');
      setErrors({});
    } else {
      // Final step - submit
      console.log('📝 Last step reached, submitting...');
      // Wait for React to process the state updates before submitting
      await new Promise(resolve => setTimeout(resolve, 50));
      await handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      // Restore previous answer
      const prevStep = activeSteps[currentStep - 1];
      if (prevStep.field) {
        if (prevStep.field === 'full_name') {
          setCurrentAnswer(`${formData.first_name} ${formData.last_name}`.trim());
        } else if (prevStep.field === 'email_opt_in') {
          setCurrentAnswer(formData.email_opt_in ? 'Yes, keep me updated!' : 'No, thanks');
        } else {
          setCurrentAnswer(formData[prevStep.field] || '');
        }
      }
      setErrors({});
    }
  };

  const handleCancelClick = () => {
    setShowCancelConfirm(true);
  };

  const handleCancelConfirm = () => {
    setShowCancelConfirm(false);
    // Call onCancel to handle cleanup and return to landing page
    if (onCancel) {
      onCancel();
    }
  };

  const handleCancelDismiss = () => {
    setShowCancelConfirm(false);
  };

  const handleComplete = async () => {
    console.log('📝 DiAi handleComplete called with formData:', formData);
    setIsSubmitting(true);
    try {
      console.log('📝 Calling parent onComplete...');
      await onComplete(formData);
      console.log('📝 Parent onComplete finished successfully');
      // Parent is responsible for closing modal and navigation
    } catch (error) {
      console.error('❌ DiAi handleComplete caught error:', error);
      console.error('❌ Error details:', error.message, error.stack);
      setErrors({ submit: 'Something went wrong. Please try again.' });
      setIsSubmitting(false);
    }
  };

  const isAnswered = currentStepData.type === 'welcome' || currentAnswer.trim().length > 0;

  return (
    <>
      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">⚠️</span>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-800 mb-2">
                  Cancel Onboarding?
                </h3>
                <p className="text-slate-600 text-sm">
                  If you cancel now, your onboarding progress will be lost and you'll be returned to the landing page. You can always complete onboarding later by logging in.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleCancelDismiss}
                variant="outline"
                className="flex-1"
              >
                Continue Onboarding
              </Button>
              <Button
                onClick={handleCancelConfirm}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Onboarding Modal */}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-3 overflow-y-auto">
        <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl max-w-lg w-full my-3 sm:my-6 relative">
          {/* Cancel Button */}
          <button
            onClick={handleCancelClick}
            className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 transition-colors z-10"
            title="Cancel onboarding"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Digital Ink AI Logo - Prominent Header */}
          <div className="flex justify-center items-center py-2 sm:py-3 px-3 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
            <img 
              src="/Digital_Ink_AI_Logo.png" 
              alt="Digital Ink AI" 
              className="h-6 sm:h-8 w-auto"
            />
          </div>

        <Card className="border-0 shadow-none">
          <CardContent className="p-3 sm:p-4 md:p-5">
            {/* DiAi Avatar and Message */}
            <div className="mb-4">
              <div className="flex items-start gap-2 mb-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_author-ai-beta/artifacts/lad4xvs5_DiAi_Image.jpg"
                  alt="DiAi"
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-blue-200 shadow-md flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg rounded-tl-none p-2 sm:p-3">
                    <p className="text-slate-800 text-xs sm:text-sm leading-snug">
                      {currentStepData.diaiMessage()}
                    </p>
                    {currentStepData.followUp && formData[currentStepData.field] && (
                      <p className="text-blue-700 text-[10px] sm:text-xs leading-snug mt-2 pt-2 border-t border-blue-200">
                        {currentStepData.followUp(formData[currentStepData.field])}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Progress Indicator */}
              <div className="flex items-center gap-1.5 mb-3">
                <div className="flex-1 bg-slate-200 h-1 rounded-full overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 h-full transition-all duration-500"
                    style={{ width: `${((currentStep + 1) / activeSteps.length) * 100}%` }}
                  />
                </div>
                <span className="text-[10px] sm:text-xs text-slate-600 font-medium whitespace-nowrap">
                  {currentStep + 1} / {activeSteps.length}
                </span>
              </div>
            </div>

            {/* User Input Area */}
            {currentStepData.prompt && (
              <div className="mb-4">
                <label className="block text-slate-700 font-semibold text-sm sm:text-base mb-2">
                  {currentStepData.prompt()}
                </label>

                {currentStepData.type === 'text' && (
                  <Input
                    type="text"
                    value={currentAnswer || ''}
                    onChange={(e) => setCurrentAnswer(e.target.value)}
                    placeholder={currentStepData.placeholder}
                    className="w-full text-sm sm:text-base p-2 sm:p-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                  />
                )}

                {currentStepData.type === 'email' && (
                  <Input
                    type="email"
                    value={currentAnswer || ''}
                    onChange={(e) => setCurrentAnswer(e.target.value)}
                    placeholder={currentStepData.placeholder}
                    className="w-full text-sm sm:text-base p-2 sm:p-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                  />
                )}

                {currentStepData.type === 'password' && (
                  <Input
                    type="password"
                    value={currentAnswer || ''}
                    onChange={(e) => setCurrentAnswer(e.target.value)}
                    placeholder={currentStepData.placeholder}
                    className="w-full text-sm sm:text-base p-2 sm:p-3 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                  />
                )}

                {currentStepData.type === 'choice' && (
                  <div className="grid gap-2">
                    {currentStepData.options.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => {
                          console.log(`Choice button clicked: field="${currentStepData.field}", option="${option}"`);
                          setCurrentAnswer(option);
                        }}
                        className={`text-left p-2 sm:p-3 rounded-lg border transition-all ${
                          currentAnswer === option
                            ? 'border-blue-500 bg-blue-50 shadow-sm'
                            : 'border-slate-300 hover:border-blue-300 hover:bg-slate-50'
                        }`}
                      >
                        <span className="text-xs sm:text-sm text-slate-800">
                          {currentAnswer === option && '✓ '}
                          {option}
                        </span>
                      </button>
                    ))}
                  </div>
                )}

                {errors[currentStepData.field] && (
                  <p className="text-red-600 text-sm mt-2">{errors[currentStepData.field]}</p>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={handleBack}
                disabled={currentStep === 0 || isSubmitting}
                variant="outline"
                size="sm"
                className="flex-1 text-xs sm:text-sm py-2 sm:py-2.5"
              >
                ← Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={!isAnswered || isSubmitting}
                size="sm"
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-xs sm:text-sm py-2 sm:py-2.5"
              >
                {isSubmitting ? 'Completing...' : currentStep === activeSteps.length - 1 ? 'Complete →' : 'Next →'}
              </Button>
            </div>

            {errors.submit && (
              <p className="text-red-600 text-center mt-4">{errors.submit}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    </>
  );
};

export default DiAiOnboarding;
